import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Zap, Shield, Building2, ChevronDown, ChevronUp } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import translations from '../i18n/translations';
import './Pricing.css';

const planIcons = [<Zap size={22} />, <Shield size={22} />, <Building2 size={22} />];
const planAccents = ['#4a5568', '#00d4ff', '#ab47bc'];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-item__q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && <p className="faq-item__a">{a}</p>}
    </div>
  );
}

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const { lang } = useLang();
  const t = translations[lang].pricing;

  return (
    <div className="pricing-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero__orb" />
        <div className="page-hero__orb page-hero__orb--2" />
        <div className="container">
          <span className="section-tag">{t.tag}</span>
          <h1 className="page-hero__title">
            {t.title1} <span className="gradient-text">{t.title2}</span>
          </h1>
          <p className="page-hero__desc">{t.desc}</p>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className={!yearly ? 'billing-toggle__label--active' : 'billing-toggle__label'}>{t.monthlyLabel}</span>
            <button
              className={`billing-toggle__btn ${yearly ? 'billing-toggle__btn--on' : ''}`}
              onClick={() => setYearly(y => !y)}
              aria-label="Toggle billing period"
            >
              <span className="billing-toggle__knob" />
            </button>
            <span className={yearly ? 'billing-toggle__label--active' : 'billing-toggle__label'}>
              {t.yearlyLabel}
              <span className="billing-toggle__save">{t.saveLabel}</span>
            </span>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="plans-section">
        <div className="container plans-grid">
          {t.plans.map((plan, i) => (
            <div
              key={i}
              className={`plan-card ${plan.highlight ? 'plan-card--highlight' : ''}`}
              style={{ '--plan-accent': planAccents[i] }}
            >
              {plan.highlight && (
                <div className="plan-card__popular">{t.mostPopular}</div>
              )}
              <div className="plan-card__header">
                <div className="plan-card__icon" style={{ color: planAccents[i], background: `${planAccents[i]}18`, border: `1px solid ${planAccents[i]}35` }}>
                  {planIcons[i]}
                </div>
                <h3 className="plan-card__name">{plan.name}</h3>
                <p className="plan-card__desc">{plan.desc}</p>
              </div>

              <div className="plan-card__price">
                {plan.price.monthly === 'Custom' || plan.price.monthly === 'مخصص' ? (
                  <span className="plan-card__price-custom">{plan.price.monthly}</span>
                ) : (
                  <>
                    <span className="plan-card__currency">$</span>
                    <span className="plan-card__amount">
                      {yearly ? plan.price.yearly : plan.price.monthly}
                    </span>
                    <span className="plan-card__period">
                      {yearly ? t.perYear : t.perMonth}
                    </span>
                  </>
                )}
              </div>

              <ul className="plan-card__features">
                {plan.features.map((f, fi) => (
                  <li key={fi}>
                    <CheckCircle size={15} className="plan-card__check" style={{ color: planAccents[i] }} />
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className={plan.highlight ? 'btn-primary plan-card__cta' : 'btn-outline plan-card__cta'}
                style={plan.highlight ? {} : { borderColor: `${planAccents[i]}50`, color: planAccents[i] === '#4a5568' ? 'var(--text-secondary)' : planAccents[i] }}
              >
                {plan.cta} <ArrowRight size={15} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison note */}
      <section className="compare-note section">
        <div className="container">
          <div className="compare-note__inner">
            <div className="compare-note__item">
              <CheckCircle size={18} className="compare-note__icon" />
              <span>No credit card required to start</span>
            </div>
            <div className="compare-note__item">
              <CheckCircle size={18} className="compare-note__icon" />
              <span>Cancel anytime, no questions asked</span>
            </div>
            <div className="compare-note__item">
              <CheckCircle size={18} className="compare-note__icon" />
              <span>GDPR compliant on all plans</span>
            </div>
            <div className="compare-note__item">
              <CheckCircle size={18} className="compare-note__icon" />
              <span>24-hour onboarding support</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{t.faqTitle}</span>
            <h2 className="section-title"><span className="gradient-text">{t.faqDesc}</span></h2>
          </div>
          <div className="faq-list">
            {t.faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="cta-box">
            <div className="cta-box__glow" />
            <h2 className="cta-box__title">{t.ctaTitle}</h2>
            <p className="cta-box__desc">{t.ctaDesc}</p>
            <div className="cta-box__actions">
              <Link to="/contact" className="btn-primary">
                {t.startTrial} <ArrowRight size={16} />
              </Link>
              <Link to="/use-cases" className="btn-outline">
                {translations[lang].useCases?.tag || 'Use Cases'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
